/*
 * Copyright © 2022 EPO: European Patent Office.
 * ALL RIGHTS RESERVED
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import { Breadcrumbs, Typography } from '@material-ui/core';
import { Link } from 'react-router-dom';
import { useLinkStyles } from 'components/common/useLinkStyles';
import DraftFooter from '../../components/submission/DraftFooter';
import { RoutePath } from '../../shared/enums/RoutePath';
import PaymentForm from '../components/form/PaymentForm';
import usePayment from './usePayment';
import PaymentDrawer from '../components/drawer/PaymentDrawer';
import { useUserAccounts } from '../../redux/features/user/userAccounts/useUserAccounts';

/**
 * Payment screen that presents the submission within a sliced payment only RJS form.
 * @returns {JSX.Element}
 * @constructor
 */
function PaymentDetail() {
  document.title = 'Draft Submission - Patent E-filing';
  const linkClass = useLinkStyles();

  const { user, currentOrganization } = useUserAccounts();

  const { t } = useTranslation();
  const {
    procedure,
    submission,
    setSubmission,
    validationSchema,
    uiSchema,
    setUiSchema,
    workflowValidations,
    setWorkflowValidations,
    errors,
    setErrors,
    extraErrors,
    setExtraErrors,
    onSave,
    onSaveAndClose,
    onClose,
    isDrawerOpen,
    toggleDrawer,
    initialSubmissionProcedure,
    submitPaymentRequest,
    onSendWithoutPayment,
    isPaymentPage,
    setNonProviderPayment,
    skipValidation,
  } = usePayment();

  const breadcrumbTo = RoutePath.generateRoutePath(RoutePath.PROCEDURE_SEARCH, {
    procedure: procedure.id,
    initialSubmissionProcedure,
  });

  return (
    <>
      <div className="submissionContainer">
        <Breadcrumbs aria-label="breadcrumb" className="breadcrumbs">
          <Link className={linkClass.link} to={breadcrumbTo}>
            {t(procedure.label)}
          </Link>
          <Typography color="textPrimary">{t('submission.payment.payment-of-fees')}</Typography>
        </Breadcrumbs>

        <PaymentForm
          uiSchema={uiSchema}
          setUiSchema={setUiSchema}
          validationSchema={validationSchema}
          submission={submission}
          setSubmission={setSubmission}
          setWorkflowValidations={setWorkflowValidations}
          setErrors={setErrors}
          extraErrors={extraErrors}
          setExtraErrors={setExtraErrors}
          skipValidation={skipValidation}
        />
      </div>

      {/* TODO: replace with PaymentFooter & remove isPaymentPage when replaced  */}
      <DraftFooter
        readOnly={false}
        submission={submission}
        submissionId={submission?.submissionId}
        errors={errors}
        isValid={workflowValidations.isValid}
        uiSchema={uiSchema}
        onSave={onSave}
        onSaveAndClose={onSaveAndClose}
        onPay={submitPaymentRequest}
        setNonProviderPayment={setNonProviderPayment}
        onSendWithoutPayment={onSendWithoutPayment}
        isPaymentPage={isPaymentPage}
        onClose={onClose}
        isDrawerOpen={isDrawerOpen}
        toggleDrawer={toggleDrawer}
      />

      <PaymentDrawer
        user={user}
        currentOrganization={currentOrganization}
        readOnly={false}
        uiSchema={uiSchema}
        submission={submission}
        sectionValidations={workflowValidations?.sectionValidations}
        isDrawerOpen={isDrawerOpen}
        toggleDrawer={toggleDrawer}
      />
    </>
  );
}

export default PaymentDetail;
